/**
 * The customer portal lives in `src/app/user/*` — outside the `[locale]`
 * segment. A locale-prefixed portal URL such as `/en/user/order-...` therefore
 * has no matching route and 404s, which is how every "order now" CTA rendered
 * on an English public page used to dead-end (ticket #887).
 *
 * Portal URLs are canonically unprefixed. These helpers are the single place
 * that knows it.
 */
export const PORTAL_BASE_PATH = "/user";

export const isPortalPath = (pathname: string): boolean =>
  pathname === PORTAL_BASE_PATH || pathname.startsWith(`${PORTAL_BASE_PATH}/`);

/**
 * Turns `/en/user/order-sized-floor-plan` into `/user/order-sized-floor-plan`.
 * Returns `null` when `pathname` is not a locale-prefixed portal path, so the
 * caller can tell "nothing to do" from "rewrite to this".
 */
export const stripLocalePrefixFromPortalPath = (
  pathname: string,
  locales: readonly string[],
): string | null => {
  for (const locale of locales) {
    const prefix = `/${locale}`;
    if (!pathname.startsWith(`${prefix}/`)) continue;

    const unprefixed = pathname.slice(prefix.length);
    if (isPortalPath(unprefixed)) return unprefixed;
  }

  return null;
};

/**
 * Validates a `?redirect=` value before it is used as a post-login
 * destination. Only same-origin portal paths are accepted, so a crafted link
 * can never bounce a freshly authenticated customer to another site.
 */
export const sanitizePortalRedirect = (
  value: string | null | undefined,
): string | null => {
  if (!value || value[0] !== "/") return null;

  // `//host` and `/\host` are protocol-relative URLs — never same-origin.
  if (value[1] === "/" || value[1] === "\\") return null;

  const [pathname] = value.split(/[?#]/, 1);

  return isPortalPath(pathname) ? value : null;
};
